import { defineStore } from 'pinia';
import { ref, computed, inject } from 'vue';
import type { Playlist, Song, PlaylistMetadata } from '@maso-dj/shared-types';
import { logger } from '@maso-dj/shared-types';
import type { PlayerState } from '../types';
import { audioPlayerKey, spotifyServiceKey } from '../injection-keys';
import { authenticatedFetch } from '../services/AuthenticatedFetch';
import { useSubscriptionStore } from './subscription';
import { useDialogStore } from './dialogs';
import { useStorageUsage } from '../composables/useStorageUsage';

const STORAGE_KEY = 'masodj-current-playlist-id';

export const usePlaylistStore = defineStore('playlist', () => {
  // Inject services
  const audioPlayer = inject(audioPlayerKey)!;
  const spotifyService = inject(spotifyServiceKey)!;
  const subscriptionStore = useSubscriptionStore();
  const dialogStore = useDialogStore();
  const { refresh: refreshStorage } = useStorageUsage();

  // State
  const currentPlaylist = ref<Playlist | null>(null);
  const allPlaylists = ref<PlaylistMetadata[]>([]);
  const currentPlaylistId = ref<string | null>(null);
  const state = ref<PlayerState>({
    currentIndex: 0,
    isPlaying: false,
    isFadingOut: false,
  });
  const fadeoutDuration = ref(5);
  const fadeoutProgress = ref(0); // 0 to 1, representing progress of fadeout
  const isAborting = ref(false); // True when aborting fadeout (fading back in)
  const presenterMode = ref(false); // When true, disable all playlist editing
  const isSpotifyAuthenticated = ref(false);

  // Upload status tracking: fileId -> 'uploading' | 'polling' | 'ready'
  const uploadStatus = ref<Record<string, 'uploading' | 'polling' | 'ready'>>({});

  // Current upload tracking (for modal dialog)
  const currentUpload = ref<{ fileId: string; filename: string; status: 'uploading' | 'polling' | 'hidden' }>({
    fileId: '',
    filename: '',
    status: 'hidden'
  });

  const apiUrl = '/api/playlists';

  // localStorage helpers
  function saveCurrentPlaylistId(playlistId: string): void {
    try {
      localStorage.setItem(STORAGE_KEY, playlistId);
      logger.debug(`[localStorage] Saved playlist ID: ${playlistId}`);
    } catch (error) {
      logger.warn('Failed to save playlist ID to localStorage:', error);
    }
  }

  function loadSavedPlaylistId(): string | null {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        logger.debug(`[localStorage] Retrieved saved playlist ID: ${saved}`);
      }
      return saved;
    } catch (error) {
      logger.warn('Failed to load playlist ID from localStorage:', error);
      return null;
    }
  }

  // Computed
  const currentSong = computed<Song | null>(() => {
    if (!currentPlaylist.value || currentPlaylist.value.songs.length === 0) {
      return null;
    }
    return currentPlaylist.value.songs[state.value.currentIndex] || null;
  });

  const nextSong = computed<Song | null>(() => {
    if (!currentPlaylist.value || currentPlaylist.value.songs.length === 0) {
      return null;
    }
    const nextIndex = state.value.currentIndex + 1;
    return nextIndex < currentPlaylist.value.songs.length
      ? currentPlaylist.value.songs[nextIndex]
      : null;
  });

  const previousSong = computed<Song | null>(() => {
    if (!currentPlaylist.value || currentPlaylist.value.songs.length === 0) {
      return null;
    }
    const prevIndex = state.value.currentIndex - 1;
    return prevIndex >= 0
      ? currentPlaylist.value.songs[prevIndex]
      : null;
  });

  // Check if a song is playable (local songs always playable, Spotify songs need auth, subscription limits apply)
  const isSongPlayable = (song: Song | null): boolean => {
    if (!song) {
      return false;
    }

    // Check subscription limits (free tier can only play first 3 songs)
    if (currentPlaylist.value) {
      const songIndex = currentPlaylist.value.songs.findIndex((s: Song) => s.id === song.id);
      const limits = subscriptionStore.limits;

      // Only log when song is NOT playable (exceeds limit)
      if (limits.maxSongsPerPlaylist !== -1 && songIndex >= limits.maxSongsPerPlaylist) {
        logger.warn('[isSongPlayable] Song NOT playable - exceeds limit:', song.title, {
          songIndex,
          maxSongsPerPlaylist: limits.maxSongsPerPlaylist,
          isUnlimited: false
        });
        return false;
      }
    }

    if (song.source === 'spotify') {
      return isSpotifyAuthenticated.value;
    }
    return true;
  };

  // Initialize service callbacks
  audioPlayer.onSongEnd(() => {
    advanceToNextAndPause();
  });

  spotifyService.onPlaybackState((playbackState) => {
    // Check if track has ended
    if (playbackState && playbackState.paused && playbackState.position === 0 && state.value.isPlaying) {
      advanceToNextAndPause();
    }
  });

  // API Actions
  async function loadAllPlaylists(): Promise<void> {
    try {
      const response = await authenticatedFetch(apiUrl);

      if (!response.ok) {
        logger.error('Failed to load playlists:', response.status, response.statusText);
        const errorText = await response.text();
        logger.error('Error response:', errorText);
        allPlaylists.value = [];
        return;
      }

      const data = await response.json();
      logger.debug('Loaded playlists data:', data);

      if (!data || !data.playlists) {
        logger.error('Invalid playlists response format:', data);
        allPlaylists.value = [];
        return;
      }

      allPlaylists.value = data.playlists;
    } catch (error) {
      logger.error('Error loading playlists:', error);
      allPlaylists.value = [];
    }
  }

  /**
   * Populate download tokens for local songs in a playlist
   * Sets downloadToken field instead of generating presigned URLs upfront
   * Tokens will be resolved to URLs just-in-time when song is played
   */
  async function populateDownloadTokens(playlist: Playlist): Promise<void> {
    // Find all local songs that have fileId
    const localSongs = playlist.songs.filter(s => s.source === 'local' && s.fileId);

    if (localSongs.length === 0) {
      return; // No local files to process
    }

    // Set download tokens for all local songs
    localSongs.forEach(song => {
      if (song.fileId) {
        song.downloadToken = `S3:${song.fileId}`;
        // Clear any old URL (we'll resolve it just-in-time)
        song.url = undefined;
      }
    });

    logger.info(`[populateDownloadTokens] Set tokens for ${localSongs.length} local songs`);
  }

  async function loadPlaylist(playlistId: string): Promise<void> {
    try {
      const response = await authenticatedFetch(`${apiUrl}/${playlistId}`);
      const playlist = await response.json();

      // Set download tokens for local files (URLs will be resolved just-in-time)
      await populateDownloadTokens(playlist);

      currentPlaylist.value = playlist;
      currentPlaylistId.value = playlistId;
      state.value.currentIndex = 0;
      state.value.isPlaying = false;
      state.value.isFadingOut = false;

      // Save to localStorage whenever playlist is loaded
      saveCurrentPlaylistId(playlistId);
    } catch (error) {
      logger.error('Error loading playlist:', error);
      // Initialize with empty playlist if loading fails
      currentPlaylist.value = {
        id: playlistId,
        name: 'My Playlist',
        createdAt: new Date().toISOString(),
        songs: [],
      };
      currentPlaylistId.value = playlistId;
    }
  }

  async function loadLastPlaylistOrFirst(): Promise<void> {
    await loadAllPlaylists();

    // No playlists exist - create default
    if (!allPlaylists.value || allPlaylists.value.length === 0) {
      logger.info('No playlists found, creating default playlist');
      const defaultPlaylist = await createPlaylist('My Playlist');
      if (defaultPlaylist) {
        await loadPlaylist(defaultPlaylist.id);
      }
      return;
    }

    // Try to load saved playlist
    const savedId = loadSavedPlaylistId();

    if (savedId) {
      // Check if saved playlist still exists
      const exists = allPlaylists.value.some(p => p.id === savedId);
      if (exists) {
        logger.info('Loading last selected playlist:', savedId);
        await loadPlaylist(savedId);
        return;
      } else {
        logger.info('Saved playlist no longer exists, loading first playlist');
      }
    }

    // Fallback: load first playlist
    logger.info('Loading first playlist:', allPlaylists.value[0].id);
    await loadPlaylist(allPlaylists.value[0].id);
  }

  async function createPlaylist(name: string): Promise<Playlist | null> {
    if (presenterMode.value) {
      logger.warn('Cannot create playlist in presenter mode');
      return null;
    }

    try {
      const response = await authenticatedFetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create playlist: ${response.statusText}`);
      }

      const result = await response.json();
      logger.info('Playlist created:', result.playlist.name);
      await loadAllPlaylists();
      return result.playlist;
    } catch (error) {
      logger.error('Error creating playlist:', error);
      return null;
    }
  }

  async function renamePlaylist(playlistId: string, newName: string): Promise<boolean> {
    if (presenterMode.value) {
      logger.warn('Cannot rename playlist in presenter mode');
      return false;
    }

    try {
      const response = await authenticatedFetch(`${apiUrl}/${playlistId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: newName }),
      });

      if (!response.ok) {
        throw new Error(`Failed to rename playlist: ${response.statusText}`);
      }

      const result = await response.json();
      logger.info('Playlist renamed:', result.playlist.name);

      // Update local playlist if it's the current one
      if (currentPlaylist.value && currentPlaylist.value.id === playlistId) {
        currentPlaylist.value.name = newName;
      }

      await loadAllPlaylists();
      return true;
    } catch (error) {
      logger.error('Error renaming playlist:', error);
      return false;
    }
  }

  async function deletePlaylist(playlistId: string): Promise<boolean> {
    if (presenterMode.value) {
      logger.warn('Cannot delete playlist in presenter mode');
      return false;
    }

    try {
      const response = await authenticatedFetch(`${apiUrl}/${playlistId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Failed to delete playlist: ${response.statusText}`);
      }

      logger.info('Playlist deleted');
      await loadAllPlaylists();
      return true;
    } catch (error) {
      logger.error('Error deleting playlist:', error);
      return false;
    }
  }

  async function savePlaylist(): Promise<void> {
    if (!currentPlaylist.value) {
      return;
    }

    try {
      const response = await authenticatedFetch(`${apiUrl}/${currentPlaylist.value.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ songs: currentPlaylist.value.songs }),
      });

      if (!response.ok) {
        throw new Error(`Failed to save playlist: ${response.statusText}`);
      }

      const result = await response.json();
      logger.info('Playlist saved:', result.playlist.name);
    } catch (error) {
      logger.error('Error saving playlist:', error);
    }
  }

  // Song Management Actions

  /**
   * Set upload status for a file (for UI feedback)
   */
  function setUploadStatus(fileId: string, status: 'uploading' | 'polling' | 'ready', filename?: string): void {
    if (status === 'ready') {
      delete uploadStatus.value[fileId];
      // Hide modal when upload is complete
      if (currentUpload.value.fileId === fileId) {
        currentUpload.value.status = 'hidden';
      }
    } else {
      uploadStatus.value[fileId] = status;
      // Show modal with current status
      currentUpload.value = {
        fileId,
        filename: filename || 'File',
        status
      };
    }
  }

  async function addSong(song: Song): Promise<void> {
    if (presenterMode.value) {
      logger.warn('Cannot add song in presenter mode');
      return;
    }

    if (!currentPlaylist.value) {
      logger.error('No playlist loaded. Cannot add song.');
      return;
    }

    currentPlaylist.value.songs.push(song);
    await savePlaylist();

    // Set download tokens for newly added local files (URLs will be resolved just-in-time)
    await populateDownloadTokens(currentPlaylist.value);
  }

  async function moveSongUp(index: number): Promise<void> {
    if (presenterMode.value) {
      logger.warn('Cannot move song in presenter mode');
      return;
    }

    if (!currentPlaylist.value || index <= 0 || index >= currentPlaylist.value.songs.length) {
      return;
    }

    // Swap with previous song
    const temp = currentPlaylist.value.songs[index];
    currentPlaylist.value.songs[index] = currentPlaylist.value.songs[index - 1];
    currentPlaylist.value.songs[index - 1] = temp;

    // Update current index if necessary
    if (state.value.currentIndex === index) {
      state.value.currentIndex = index - 1;
    } else if (state.value.currentIndex === index - 1) {
      state.value.currentIndex = index;
    }

    await savePlaylist();
  }

  async function moveSongDown(index: number): Promise<void> {
    if (presenterMode.value) {
      logger.warn('Cannot move song in presenter mode');
      return;
    }

    if (!currentPlaylist.value || index < 0 || index >= currentPlaylist.value.songs.length - 1) {
      return;
    }

    // Swap with next song
    const temp = currentPlaylist.value.songs[index];
    currentPlaylist.value.songs[index] = currentPlaylist.value.songs[index + 1];
    currentPlaylist.value.songs[index + 1] = temp;

    // Update current index if necessary
    if (state.value.currentIndex === index) {
      state.value.currentIndex = index + 1;
    } else if (state.value.currentIndex === index + 1) {
      state.value.currentIndex = index;
    }

    await savePlaylist();
  }

  async function removeSong(index: number): Promise<void> {
    if (presenterMode.value) {
      logger.warn('Cannot remove song in presenter mode');
      return;
    }

    if (!currentPlaylist.value || index < 0 || index >= currentPlaylist.value.songs.length) {
      return;
    }

    // Cannot remove currently playing song
    if (index === state.value.currentIndex && state.value.isPlaying) {
      logger.warn('Cannot remove currently playing song');
      return;
    }

    const song = currentPlaylist.value.songs[index];
    const playlistId = currentPlaylist.value.id;

    try {
      // Call backend to remove song from playlist (backend handles file deletion)
      const response = await authenticatedFetch(
        `/api/playlists/${playlistId}/songs/${song.id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to remove song: ${response.statusText}`);
      }

      const result = await response.json();
      logger.debug(result.message);

      // Backend returns updated playlist - use it directly
      if (result.playlist) {
        // Generate fresh download URLs for local files
        await populateDownloadTokens(result.playlist);
        currentPlaylist.value = result.playlist;
      } else {
        // Fallback: reload playlist from server
        await loadPlaylist(playlistId);
      }

      // Update current index if necessary
      if (index < state.value.currentIndex) {
        state.value.currentIndex--;
      } else if (index === state.value.currentIndex && currentPlaylist.value && currentPlaylist.value.songs.length > 0) {
        state.value.currentIndex = Math.min(state.value.currentIndex, currentPlaylist.value.songs.length - 1);
      }

      await loadAllPlaylists();

      // Refresh storage usage after successful deletion
      refreshStorage().catch(err => {
        logger.error('Failed to refresh storage usage:', err);
      });

    } catch (error) {
      logger.error('Error removing song:', error);
      await dialogStore.alert(
        `Failed to remove song: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { confirmText: 'OK', title: 'Error' }
      );
    }
  }

  async function duplicateSong(index: number): Promise<void> {
    if (presenterMode.value) {
      logger.warn('Cannot duplicate song in presenter mode');
      return;
    }

    if (!currentPlaylist.value || index < 0 || index >= currentPlaylist.value.songs.length) {
      return;
    }

    const originalSong = currentPlaylist.value.songs[index];

    const duplicateSong: Song = {
      ...originalSong,
      id: `duplicate-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };

    currentPlaylist.value.songs.splice(index + 1, 0, duplicateSong);
    await savePlaylist();
    await loadAllPlaylists();
  }

  async function updateSongNotes(index: number, notes: string): Promise<void> {
    if (presenterMode.value) {
      logger.warn('Cannot update song notes in presenter mode');
      return;
    }

    if (!currentPlaylist.value || index < 0 || index >= currentPlaylist.value.songs.length) {
      return;
    }

    // Set notes to undefined if empty, otherwise set to the trimmed value
    const trimmedNotes = notes.trim();
    currentPlaylist.value.songs[index].notes = trimmedNotes || undefined;
    await savePlaylist();
    await loadAllPlaylists();
  }

  async function updateSongTimeRange(index: number, startTime: number, endTime: number): Promise<void> {
    if (presenterMode.value) {
      logger.warn('Cannot update song time range in presenter mode');
      return;
    }

    if (!currentPlaylist.value || index < 0 || index >= currentPlaylist.value.songs.length) {
      logger.warn('[updateSongTimeRange] Invalid index or no playlist:', { index, hasPlaylist: !!currentPlaylist.value });
      return;
    }

    const song = currentPlaylist.value.songs[index];
    logger.info('[updateSongTimeRange] Updating song time range:', {
      song: song.title,
      oldStart: song.startTime,
      oldEnd: song.endTime,
      newStart: startTime,
      newEnd: endTime
    });
    song.startTime = startTime;
    song.endTime = endTime;
    await savePlaylist();
    // Don't reload - it might cause the currentSong ref to become stale
    // await loadAllPlaylists();
  }

  async function reorderSong(fromIndex: number, toIndex: number): Promise<void> {
    if (presenterMode.value) {
      logger.warn('Cannot reorder song in presenter mode');
      return;
    }

    if (!currentPlaylist.value ||
      fromIndex < 0 || fromIndex >= currentPlaylist.value.songs.length ||
      toIndex < 0 || toIndex >= currentPlaylist.value.songs.length ||
      fromIndex === toIndex) {
      return;
    }

    // Remove song from old position
    const [song] = currentPlaylist.value.songs.splice(fromIndex, 1);

    // Insert at new position
    currentPlaylist.value.songs.splice(toIndex, 0, song);

    // Update current index if necessary
    if (state.value.currentIndex === fromIndex) {
      state.value.currentIndex = toIndex;
    } else if (fromIndex < state.value.currentIndex && toIndex >= state.value.currentIndex) {
      state.value.currentIndex--;
    } else if (fromIndex > state.value.currentIndex && toIndex <= state.value.currentIndex) {
      state.value.currentIndex++;
    }

    await savePlaylist();
    await loadAllPlaylists();
  }

  // Playback Actions
  async function skipForward(): Promise<void> {
    if (!currentPlaylist.value || currentPlaylist.value.songs.length === 0) {
      return;
    }

    // Immediately stop any playing audio before moving to next song
    // Next/prev should ALWAYS stop playback (not auto-play the new song)
    if (state.value.isPlaying || state.value.isFadingOut) {
      state.value.isPlaying = false;
      state.value.isFadingOut = false;
      await stopPlayback();
    }

    const totalSongs = currentPlaylist.value.songs.length;
    let attempts = 0;
    let nextIndex = state.value.currentIndex;

    // Keep skipping until we find a playable song or we've checked all songs
    do {
      nextIndex = (nextIndex + 1) % totalSongs;
      attempts++;

      const nextSongCandidate = currentPlaylist.value.songs[nextIndex];

      // If we found a playable song, or we've checked all songs
      if (isSongPlayable(nextSongCandidate) || attempts >= totalSongs) {
        break;
      }
    } while (attempts < totalSongs);

    state.value.currentIndex = nextIndex;
    // Don't auto-play the new song - just stop and move pointer
  }

  async function advanceToNextAndPause(): Promise<void> {
    if (!currentPlaylist.value || currentPlaylist.value.songs.length === 0) {
      return;
    }

    const totalSongs = currentPlaylist.value.songs.length;
    let attempts = 0;
    let nextIndex = state.value.currentIndex;

    // Keep skipping until we find a playable song or we've checked all songs
    do {
      nextIndex = (nextIndex + 1) % totalSongs;
      attempts++;

      const nextSongCandidate = currentPlaylist.value.songs[nextIndex];

      // If we found a playable song, or we've checked all songs
      if (isSongPlayable(nextSongCandidate) || attempts >= totalSongs) {
        break;
      }
    } while (attempts < totalSongs);

    state.value.currentIndex = nextIndex;

    // Always stop playback and pause when song ends naturally
    state.value.isPlaying = false;
    await stopPlayback();
  }

  async function skipBackward(): Promise<void> {
    if (!currentPlaylist.value || currentPlaylist.value.songs.length === 0) {
      return;
    }

    // Immediately stop any playing audio before moving to previous song
    // Next/prev should ALWAYS stop playback (not auto-play the new song)
    if (state.value.isPlaying || state.value.isFadingOut) {
      state.value.isPlaying = false;
      state.value.isFadingOut = false;
      await stopPlayback();
    }

    const totalSongs = currentPlaylist.value.songs.length;
    let attempts = 0;
    let prevIndex = state.value.currentIndex;

    // Keep skipping until we find a playable song or we've checked all songs
    do {
      prevIndex = prevIndex === 0 ? totalSongs - 1 : prevIndex - 1;
      attempts++;

      const prevSongCandidate = currentPlaylist.value.songs[prevIndex];

      // If we found a playable song, or we've checked all songs
      if (isSongPlayable(prevSongCandidate) || attempts >= totalSongs) {
        break;
      }
    } while (attempts < totalSongs);

    state.value.currentIndex = prevIndex;
    // Don't auto-play the new song - just stop and move pointer
  }

  async function setCurrentSong(index: number): Promise<void> {
    if (!currentPlaylist.value || index < 0 || index >= currentPlaylist.value.songs.length) {
      return;
    }

    // Stop any current playback immediately
    if (state.value.isPlaying) {
      await stopPlayback();
      state.value.isPlaying = false;
      state.value.isFadingOut = false;
    }

    state.value.currentIndex = index;
  }

  async function togglePlayPause(): Promise<void> {
    if (state.value.isPlaying) {
      // If aborting (fading back in), cancel the abort and start a new fadeout
      if (isAborting.value) {
        // Stop the abort process and start a new fadeout
        isAborting.value = false;
        state.value.isFadingOut = true;
        fadeoutProgress.value = 0;

        const song = currentSong.value;
        if (song && song.source === 'spotify' && spotifyService) {
          // Cancel the abort by starting a new fadeout
          await spotifyService.fadeOutAndStop(fadeoutDuration.value, async () => {
            state.value.isPlaying = false;
            state.value.isFadingOut = false;
            fadeoutProgress.value = 0;
            await skipForward();
          }, (progress) => {
            fadeoutProgress.value = progress;
          });
        } else {
          // For local audio, start a new fadeout
          audioPlayer.fadeOutAndStop(() => {
            state.value.isPlaying = false;
            state.value.isFadingOut = false;
            fadeoutProgress.value = 0;
            skipForward();
          }, (progress) => {
            fadeoutProgress.value = progress;
          });
        }
      }
      // If already fading out (but not aborting), stop immediately and advance
      else if (state.value.isFadingOut) {
        await stopPlayback(() => {
          state.value.isPlaying = false;
          state.value.isFadingOut = false;
          fadeoutProgress.value = 0;
          skipForward();
        });
      } else {
        // Start fadeout
        state.value.isFadingOut = true;
        fadeoutProgress.value = 0;

        const song = currentSong.value;
        if (song && song.source === 'spotify' && spotifyService) {
          // For Spotify, use volume-based fadeout
          await spotifyService.fadeOutAndStop(fadeoutDuration.value, async () => {
            state.value.isPlaying = false;
            state.value.isFadingOut = false;
            fadeoutProgress.value = 0;
            await skipForward();
          }, (progress) => {
            fadeoutProgress.value = progress;
          });
        } else {
          // For local audio, use fadeout
          audioPlayer.fadeOutAndStop(() => {
            state.value.isPlaying = false;
            state.value.isFadingOut = false;
            fadeoutProgress.value = 0;
            skipForward();
          }, (progress) => {
            fadeoutProgress.value = progress;
          });
        }
      }
    } else {
      const song = currentSong.value;
      if (song) {
        // Check if current song is playable
        if (!isSongPlayable(song)) {
          logger.warn('Current song is not playable, skipping to next playable song');
          // Try to find a playable song
          await skipForward();
          const nextPlayableSong = currentSong.value;
          if (nextPlayableSong && isSongPlayable(nextPlayableSong)) {
            await playSong(nextPlayableSong);
            state.value.isPlaying = true;
          } else {
            logger.warn('No playable songs in playlist');
          }
        } else {
          await playSong(song);
          state.value.isPlaying = true;
        }
      }
    }
  }

  async function abortFadeout(): Promise<void> {
    if (!state.value.isFadingOut) {
      return;
    }

    // Set aborting flag immediately to hide cancel widget
    isAborting.value = true;

    // Abort the fadeout and restore volume
    const song = currentSong.value;
    const fadeInDuration = fadeoutDuration.value / 2; // Twice the speed = half the duration

    // Capture the current progress at the moment of abort
    const startProgress = fadeoutProgress.value;

    // Callback to reset state when fade-in completes
    const onComplete = (): void => {
      state.value.isFadingOut = false;
      fadeoutProgress.value = 0;
      isAborting.value = false;
    };

    // Progress callback to update the progress bars during fade-in
    // progress goes from 1 to 0, we need to map it to startProgress to 0
    const onProgress = (progress: number): void => {
      fadeoutProgress.value = startProgress * progress;
    };

    if (song && song.source === 'spotify' && spotifyService) {
      await spotifyService.abortFadeout(fadeInDuration, onProgress, onComplete);
    } else {
      audioPlayer.abortFadeout(fadeInDuration, onProgress, onComplete);
    }
  }

  async function playSong(song: Song): Promise<void> {
    await stopPlayback();

    if (song.source === 'spotify' && song.spotifyUri && spotifyService) {
      const deviceId = spotifyService.getDeviceId();

      logger.debug('Attempting to play Spotify track:', {
        title: song.title,
        uri: song.spotifyUri,
        hasSpotifyService: !!spotifyService,
        deviceId: deviceId,
        startTime: song.startTime,
        endTime: song.endTime
      });

      // If device isn't ready, fall back to preview URL immediately (don't wait for error)
      if (!deviceId) {
        logger.info('Spotify device not initialized, using preview URL fallback');
        if (song.url) {
          logger.debug('Playing 30-second preview:', song.url);
          await audioPlayer.play(song).catch(error => {
            logger.error('Error playing preview URL:', error);
            dialogStore.alert(`Failed to play preview: ${error instanceof Error ? error.message : 'Unknown error'}`, { confirmText: 'OK' });
          });
        } else {
          logger.warn('No preview URL available for Spotify track:', song.title);
          // Skip to next song silently (many tracks don't have previews)
          await skipForward();
        }
        return;
      }

      try {
        await spotifyService.play(song.spotifyUri, song.startTime, song.endTime);
        logger.info('Playing Spotify track via Web Playback SDK:', song.title);
      } catch (error) {
        logger.error('Error playing Spotify track:', error);

        // Fallback to preview URL if available
        if (song.url) {
          logger.info('Falling back to preview URL (30-second preview):', song.url);
          await audioPlayer.play(song).catch(error => {
            logger.error('Error playing preview URL:', error);
            dialogStore.alert(`Failed to play preview: ${error instanceof Error ? error.message : 'Unknown error'}`, { confirmText: 'OK' });
          });
        } else {
          logger.warn('No preview URL available for Spotify track:', song.title);
          // Skip to next song silently (many tracks don't have previews)
          await skipForward();
        }
      }
    } else {
      await audioPlayer.play(song).catch(error => {
        logger.error('Error playing local track:', error);
        dialogStore.alert(`Failed to play song: ${error instanceof Error ? error.message : 'Unknown error'}`, { confirmText: 'OK' });
      });
      logger.info('Playing local track:', song.title);
    }
  }

  async function stopPlayback(callback?: () => void): Promise<void> {
    // Stop both Spotify and local audio to ensure clean transitions
    // between different audio sources (Spotify ↔ local)
    if (spotifyService) {
      await spotifyService.pause();
      await spotifyService.setVolume(1.0);
    }
    audioPlayer.stopImmediately();

    if (callback) {
      callback();
    }
  }

  function setFadeoutDuration(seconds: number): void {
    fadeoutDuration.value = seconds;
    audioPlayer.setFadeoutDuration(seconds);
  }

  function setPresenterMode(enabled: boolean): void {
    presenterMode.value = enabled;
  }

  function setSpotifyAuthenticated(authenticated: boolean): void {
    isSpotifyAuthenticated.value = authenticated;
  }

  return {
    // State
    currentPlaylist,
    allPlaylists,
    currentPlaylistId,
    state,
    fadeoutDuration,
    fadeoutProgress,
    isAborting,
    presenterMode,
    isSpotifyAuthenticated,
    uploadStatus,
    currentUpload,

    // Computed
    currentSong,
    nextSong,
    previousSong,
    isSongPlayable,

    // Actions
    loadAllPlaylists,
    loadPlaylist,
    loadLastPlaylistOrFirst,
    createPlaylist,
    renamePlaylist,
    deletePlaylist,
    setUploadStatus,
    addSong,
    moveSongUp,
    moveSongDown,
    removeSong,
    duplicateSong,
    updateSongNotes,
    updateSongTimeRange,
    reorderSong,
    skipForward,
    skipBackward,
    setCurrentSong,
    togglePlayPause,
    playSong,
    abortFadeout,
    setFadeoutDuration,
    setPresenterMode,
    setSpotifyAuthenticated,
  };
});
